import { useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Phone, Mail, MapPin, MessageCircle, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("नाम और संदेश आवश्यक है");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("आपका संदेश भेज दिया गया है। हम शीघ्र सम्पर्क करेंगे।");
      setForm(EMPTY);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PublicHeader />
      <main className="max-w-6xl mx-auto px-4 py-12" data-testid="contact-page">
        <div className="mb-10 text-center">
          <div className="tag-cat mb-3">संपर्क करें</div>
          <h1 className="font-devnagri-heading text-4xl sm:text-5xl font-black text-[#0F172A]">हमसे जुड़ें</h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">अपने विचार, समाचार सुझाव, विज्ञापन पूछताछ या फीडबैक भेजें — हमारी टीम 24 घंटे के भीतर उत्तर देगी।</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info Cards */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-[#EA580C]/10 text-[#EA580C] flex items-center justify-center"><Phone className="w-5 h-5"/></div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-admin font-semibold">फ़ोन</div>
              </div>
              <a href="tel:+918463860303" className="text-lg font-bold text-[#0F172A] hover:text-[#1E3A8A]" data-testid="contact-phone">+91 8463860303</a>
              <div className="text-xs text-slate-500 mt-1">सोम-शनि · 9AM — 8PM</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><MessageCircle className="w-5 h-5"/></div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-admin font-semibold">WhatsApp</div>
              </div>
              <a href="https://wa.me/918463860303" target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-[#0F172A] hover:text-emerald-600" data-testid="contact-whatsapp">+91 8463860303</a>
              <div className="text-xs text-slate-500 mt-1">तुरंत उत्तर के लिए</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-sky-500/10 text-sky-600 flex items-center justify-center"><Mail className="w-5 h-5"/></div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-admin font-semibold">ईमेल</div>
              </div>
              <a href="mailto:mdjkbaba786@gmail.com" className="text-base font-semibold text-[#0F172A] hover:text-sky-600 break-all" data-testid="contact-email">mdjkbaba786@gmail.com</a>
            </div>

            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center"><MapPin className="w-5 h-5"/></div>
                <div className="text-xs uppercase tracking-widest text-slate-300 font-admin font-semibold">प्रधान कार्यालय</div>
              </div>
              <div className="font-devnagri-heading text-lg font-bold leading-snug" data-testid="contact-address">
                सिरसी नवापारा, मेन रोड<br/>
                सूरजपुर, छत्तीसगढ़<br/>
                भारत — पिन 497231
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-300 mt-4 pt-4 border-t border-white/10">
                <Clock className="w-3.5 h-3.5"/> सोमवार — शनिवार · सुबह 9 से रात 8 तक
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 shadow-sm space-y-5" data-testid="contact-form">
              <h2 className="font-devnagri-heading text-2xl font-bold text-[#0F172A]">संदेश भेजें</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name">आपका नाम *</Label>
                  <Input id="c-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="contact-input-name"/>
                </div>
                <div>
                  <Label htmlFor="c-phone">फ़ोन नंबर</Label>
                  <Input id="c-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="contact-input-phone"/>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-email">ईमेल</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="contact-input-email"/>
                </div>
                <div>
                  <Label htmlFor="c-subject">विषय</Label>
                  <Input id="c-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="समाचार सुझाव / विज्ञापन / अन्य" data-testid="contact-input-subject"/>
                </div>
              </div>
              <div>
                <Label htmlFor="c-msg">आपका संदेश *</Label>
                <Textarea id="c-msg" rows={6} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required data-testid="contact-input-message"/>
              </div>
              <Button type="submit" disabled={busy} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="contact-submit">
                {busy ? "भेजा जा रहा है…" : (<><Send className="w-4 h-4 mr-2"/>संदेश भेजें</>)}
              </Button>
            </form>

            {/* Google Map */}
            <div className="mt-8 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="contact-map">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#DC2626]"/>
                <div className="font-bold text-[#0F172A]">कार्यालय स्थान</div>
                <a href="https://www.google.com/maps/search/?api=1&query=Sirsi+Nawapara+Surajpur+Chhattisgarh+497231" target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-[#1E3A8A] hover:underline">Google Maps पर खोलें</a>
              </div>
              <iframe
                title="Samachar Office Map"
                src="https://www.google.com/maps?q=Sirsi+Nawapara+Surajpur+Chhattisgarh+497231&output=embed"
                width="100%" height="320" style={{ border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </>
  );
}
